import React, {useState} from 'react';
import {motion} from 'framer-motion';
import {Link} from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';
import {useAuth0Data} from '../../../context/Auth0DataContext';
import {format, parseISO, isPast, isToday} from 'date-fns';

const {FiCheckSquare, FiSquare, FiClock, FiAlertTriangle, FiUser, FiFilter, FiPlus, FiExternalLink, FiSearch, FiX} = FiIcons;

const TaskManagementWidget = () => {
  const {data, updateItem} = useAuth0Data();
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('dueDate');
  const [sortOrder, setSortOrder] = useState('asc');

  const getAllTasks = () => {
    return data.projects.flatMap(project =>
      project.tasks.map(task => ({
        ...task,
        projectTitle: project.title,
        projectId: project.id
      }))
    );
  };

  const handleTaskToggle = (task) => {
    const newStatus = task.status === 'completed' ? 'todo' : 'completed';
    const project = data.projects.find(p => p.id === task.projectId);
    if (project) {
      const updatedTasks = project.tasks.map(t =>
        t.id === task.id ? {...t, status: newStatus} : t
      );
      updateItem('projects', project.id, {...project, tasks: updatedTasks});
    }
  };

  const getFilteredTasks = () => {
    const allTasks = getAllTasks();

    // Filter by status
    let filteredTasks = allTasks;
    switch (filter) {
      case 'pending':
        filteredTasks = allTasks.filter(task => 
          task.status === 'todo' || task.status === 'in-progress'
        );
        break;
      case 'overdue':
        filteredTasks = allTasks.filter(task => 
          (task.status === 'todo' || task.status === 'in-progress') &&
          task.dueDate && isPast(parseISO(task.dueDate)) && !isToday(parseISO(task.dueDate))
        );
        break;
      case 'completed':
        filteredTasks = allTasks.filter(task => task.status === 'completed');
        break;
      default:
        break;
    }

    // Filter by search term
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      filteredTasks = filteredTasks.filter(task =>
        task.title.toLowerCase().includes(search) ||
        task.assignee.toLowerCase().includes(search) ||
        task.projectTitle.toLowerCase().includes(search) ||
        (task.dueDate && format(parseISO(task.dueDate), 'MMM dd, yyyy').toLowerCase().includes(search))
      );
    }

    // Sort tasks
    filteredTasks.sort((a, b) => {
      let aValue, bValue;
      switch (sortBy) {
        case 'title':
          aValue = a.title.toLowerCase();
          bValue = b.title.toLowerCase();
          break;
        case 'assignee':
          aValue = a.assignee.toLowerCase();
          bValue = b.assignee.toLowerCase();
          break;
        case 'project':
          aValue = a.projectTitle.toLowerCase();
          bValue = b.projectTitle.toLowerCase();
          break;
        case 'dueDate':
        default:
          if (!a.dueDate && !b.dueDate) return 0;
          if (!a.dueDate) return 1;
          if (!b.dueDate) return -1;
          aValue = new Date(a.dueDate);
          bValue = new Date(b.dueDate);
          break;
      }

      if (sortBy === 'dueDate') {
        return sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
      } else {
        if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
        return 0;
      }
    });

    return filteredTasks.slice(0, 10); // Show only first 10 tasks in widget
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const filteredTasks = getFilteredTasks();
  const allTasks = getAllTasks();

  const pendingTasks = allTasks.filter(task => 
    task.status === 'todo' || task.status === 'in-progress'
  ).length;
  const overdueTasks = allTasks.filter(task => 
    (task.status === 'todo' || task.status === 'in-progress') &&
    task.dueDate && isPast(parseISO(task.dueDate)) && !isToday(parseISO(task.dueDate))
  ).length;
  const completedTasks = allTasks.filter(task => task.status === 'completed').length;

  const getTaskIcon = (task) => {
    if (task.status === 'completed') return FiCheckSquare;
    if (task.dueDate && isPast(parseISO(task.dueDate)) && !isToday(parseISO(task.dueDate))) {
      return FiAlertTriangle;
    }
    return FiSquare;
  };

  const getTaskColor = (task) => {
    if (task.status === 'completed') return 'text-green-600';
    if (task.dueDate && isPast(parseISO(task.dueDate)) && !isToday(parseISO(task.dueDate))) {
      return 'text-red-600';
    }
    if (task.dueDate && isToday(parseISO(task.dueDate))) return 'text-yellow-600';
    return 'text-gray-600';
  };

  if (allTasks.length === 0) {
    return (
      <Link to="/project-management/tasks" className="block">
        <motion.div
          whileHover={{y: -2}}
          className="bg-white rounded-xl shadow-soft border border-gray-100 p-6 hover:shadow-md transition-all duration-200 cursor-pointer group"
        >
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                <SafeIcon icon={FiCheckSquare} className="w-5 h-5 text-indigo-600" />
              </div>
              <h3 className="ml-3 text-lg font-semibold text-gray-900">Task Management</h3>
            </div>
            <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200">
              <SafeIcon icon={FiExternalLink} className="w-4 h-4 text-indigo-600" />
            </div>
          </div>

          <div className="text-center py-12 text-gray-500">
            <SafeIcon icon={FiPlus} className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No tasks found.</p>
            <p className="text-sm">Click to go to Task Management →</p>
          </div>
        </motion.div>
      </Link>
    );
  }

  return (
    <Link to="/project-management/tasks" className="block">
      <motion.div
        whileHover={{y: -2}}
        className="bg-white rounded-xl shadow-soft border border-gray-100 p-6 hover:shadow-md transition-all duration-200 cursor-pointer group"
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
              <SafeIcon icon={FiCheckSquare} className="w-5 h-5 text-indigo-600" />
            </div>
            <h3 className="ml-3 text-lg font-semibold text-gray-900">Task Management</h3>
          </div>
          <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200">
            <SafeIcon icon={FiExternalLink} className="w-4 h-4 text-indigo-600" />
          </div>
        </div>

        {/* Task Stats */}
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="text-center p-2 bg-blue-50 rounded-lg">
            <p className="text-lg font-bold text-blue-600">{pendingTasks}</p>
            <p className="text-xs text-gray-600">Pending</p>
          </div>
          <div className="text-center p-2 bg-red-50 rounded-lg">
            <p className="text-lg font-bold text-red-600">{overdueTasks}</p>
            <p className="text-xs text-gray-600">Overdue</p>
          </div>
          <div className="text-center p-2 bg-green-50 rounded-lg">
            <p className="text-lg font-bold text-green-600">{completedTasks}</p>
            <p className="text-xs text-gray-600">Completed</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-2 mb-4" onClick={(e) => e.stopPropagation()}>
          <div className="relative flex-1">
            <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search tasks..."
              className="w-full pl-10 pr-8 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <SafeIcon icon={FiX} className="w-4 h-4" />
              </button>
            )}
          </div>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          >
            <option value="all">All Tasks</option>
            <option value="pending">Pending</option>
            <option value="overdue">Overdue</option>
            <option value="completed">Completed</option>
          </select>
        </div>

        {/* Task Table */}
        <div className="overflow-x-auto" onClick={(e) => e.stopPropagation()}>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 text-left">
                <th className="pb-2 text-xs font-medium text-gray-500 uppercase tracking-wide">Status</th>
                <th 
                  className="pb-2 text-xs font-medium text-gray-500 uppercase tracking-wide cursor-pointer hover:text-gray-700"
                  onClick={() => handleSort('title')}
                >
                  Task {sortBy === 'title' && (sortOrder === 'asc' ? '↑' : '↓')}
                </th>
                <th 
                  className="pb-2 text-xs font-medium text-gray-500 uppercase tracking-wide cursor-pointer hover:text-gray-700"
                  onClick={() => handleSort('assignee')}
                >
                  Assignee {sortBy === 'assignee' && (sortOrder === 'asc' ? '↑' : '↓')}
                </th>
                <th 
                  className="pb-2 text-xs font-medium text-gray-500 uppercase tracking-wide cursor-pointer hover:text-gray-700"
                  onClick={() => handleSort('project')}
                >
                  Project {sortBy === 'project' && (sortOrder === 'asc' ? '↑' : '↓')}
                </th>
                <th 
                  className="pb-2 text-xs font-medium text-gray-500 uppercase tracking-wide cursor-pointer hover:text-gray-700"
                  onClick={() => handleSort('dueDate')}
                >
                  Due Date {sortBy === 'dueDate' && (sortOrder === 'asc' ? '↑' : '↓')}
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredTasks.map((task, index) => (
                <motion.tr
                  key={`${task.id}-${task.projectId}`}
                  initial={{opacity: 0, y: 10}}
                  animate={{opacity: 1, y: 0}}
                  transition={{delay: index * 0.02}}
                  className="border-b border-gray-100 hover:bg-gray-50"
                >
                  <td className="py-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleTaskToggle(task);
                      }}
                      className={`${getTaskColor(task)} hover:scale-110 transition-transform`}
                    >
                      <SafeIcon icon={getTaskIcon(task)} className="w-4 h-4" />
                    </button>
                  </td>
                  <td className="py-2">
                    <div className={`font-medium ${task.status === 'completed' ? 'line-through text-gray-500' : 'text-gray-900'}`}>
                      {task.title}
                    </div>
                  </td>
                  <td className="py-2">
                    <div className="flex items-center text-gray-600">
                      <SafeIcon icon={FiUser} className="w-3 h-3 mr-1" />
                      {task.assignee}
                    </div>
                  </td>
                  <td className="py-2">
                    <span className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded-full">
                      {task.projectTitle}
                    </span>
                  </td>
                  <td className="py-2">
                    {task.dueDate ? (
                      <div className={`flex items-center text-xs ${getTaskColor(task)}`}>
                        <SafeIcon icon={FiClock} className="w-3 h-3 mr-1" />
                        {format(parseISO(task.dueDate), 'MMM dd')}
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400">No date</span>
                    )}
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredTasks.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            <SafeIcon icon={FiCheckSquare} className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>No tasks found for the selected filter</p>
          </div>
        )}

        {/* View All Link */}
        {allTasks.length > 10 && (
          <div className="mt-4 text-center">
            <p className="text-sm text-primary-600 hover:text-primary-700">
              View All Tasks ({allTasks.length}) →
            </p>
          </div>
        )}
      </motion.div>
    </Link>
  );
};

export default TaskManagementWidget;